import { Performance as PerformanceIcon, ViewSidebar as ViewSidebarIcon } from '@tunasong/icons'
import { AgentsSubmenu, useAgentRoster } from '@tunasong/plugin-agent/integration'
import type { BaseLayoutSpec } from '@tunasong/plugin-lib'
import { useLayout, useLayoutsExtended } from '@tunasong/plugin-lib'
import { features, selectViewEditMode, useSelector, useThunkDispatch } from '@tunasong/redux'
import { isFolderContainer } from '@tunasong/schemas'
import {
  Button,
  FavoriteIcon,
  LayoutSelector,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Presence,
  TunaEntityBreadcrumbs,
  isMobileDevice,
  useDeviceInfo,
  useDimensions,
  useLayoutNavigation,
  useSidebar,
  useUIMode,
  ToggleButton,
} from '@tunasong/ui-lib'
import cn from 'classnames'
import type { ReactNode } from 'react'
import CreateEntityButton from '../entity/create-entity-button.js'
import { Header } from '../header.js'
import { useShare } from '../share/share.js'
import type { ChromeProps } from './chrome.js'
import { EntityMenuButton } from './entity-menu.js'
import { LayoutSubmenu } from './layout-submenu.js'
import styles from './entity-header.module.css'

export interface EntityHeaderProps extends ChromeProps {
  ref?: React.Ref<HTMLDivElement>
  /** Extra controls appended to the default header toolbar. */
  additionalControls?: ReactNode
}

const performanceLayoutName = 'Performance'

export const EntityHeader = (props: EntityHeaderProps) => {
  const { entity, layoutName, showSidebar, ref, additionalControls } = props

  const { isTiny, isSmallOrSmaller } = useDimensions()
  const { isMobile } = useDeviceInfo()
  const uiMode = useUIMode()

  const layouts = useLayoutsExtended(entity)
  const { setLayout } = useLayoutNavigation({ entity })

  const navigateToLayout = (layout: BaseLayoutSpec) => setLayout(layout.name)

  const layout = useLayout({ entity, layoutName })

  const entityId = entity?.id

  const { setActiveTool, open: sidebarOpen, setOpen: setSidebarOpen } = useSidebar()

  const handleToggleSidebar = () => {
    if (!sidebarOpen) {
      setSidebarOpen(true)
      setActiveTool('Settings')
    } else {
      setSidebarOpen(false)
      setActiveTool(null)
    }
  }

  const container = layout?.container ? layout.container : {}

  /** Mobile edit toggle: shown wherever the view can start read-only (small screens and touch devices) */
  const showEditToggle = Boolean(entity && layout?.mobileEditToggle && (uiMode === 'mobile' || isMobile))
  const edit = useSelector(state => selectViewEditMode(state, entityId, !isMobileDevice()))
  const dispatch = useThunkDispatch()
  const handleToggleEdit = () => {
    if (entityId) {
      dispatch(features.ui.actions.setViewEditMode({ entityId, edit: !edit }))
    }
  }

  const { dialog: shareDialog, show: showShare } = useShare(entity ?? undefined)
  const showShareButton = Boolean(entity && !entity.trash)

  /** Folders keep "New" in the bar (it is their main action); elsewhere it lives in the overflow menu */
  const showCreateButton = Boolean(entity && isFolderContainer(entity))

  /** The dedicated Perform button replaces the Performance entry in the layout selector */
  const showPerformanceIcon = Boolean(
    entity && layouts.some(l => l.name === performanceLayoutName) && layout?.name !== performanceLayoutName
  )
  const selectorLayouts = showPerformanceIcon ? layouts.filter(l => l.name !== performanceLayoutName) : layouts
  /** Without Performance, a lone remaining layout would leave a disabled one-item selector (e.g. Folder, Setlist) */
  const showLayoutSelector = showPerformanceIcon ? selectorLayouts.length > 1 : selectorLayouts.length > 0

  /**
   * Small screens keep Back, title, Edit, Perform and More in the bar. Layout, Share, the star and the
   * sidebar toggle move into More (Share and Add to Favorites are already entity-menu items).
   */
  const foldIntoMenu = isSmallOrSmaller
  /** Small screens: the Agents pill leaves the bar for More, so the header reads the roster (and stamps first-visit cursors) */
  const agentRoster = useAgentRoster(foldIntoMenu ? entity?.id : undefined)
  const unreadAgents = foldIntoMenu ? agentRoster.unreadCount : 0
  const moreLabel =
    unreadAgents > 0
      ? `More actions, ${unreadAgents} ${unreadAgents === 1 ? 'agent has' : 'agents have'} unread messages`
      : undefined
  const showAgentsInBar = Boolean(additionalControls) && !foldIntoMenu
  const showSidebarItem = Boolean(showSidebar && isTiny)
  const leadingMenuItems = foldIntoMenu ? (
    <>
      <AgentsSubmenu roster={agentRoster} />
      {showLayoutSelector ? (
        <LayoutSubmenu layouts={selectorLayouts} selected={layout ?? undefined} onChange={navigateToLayout} />
      ) : null}
      {showSidebarItem ? (
        <MenuItem onClick={handleToggleSidebar}>
          <ListItemIcon>
            <ViewSidebarIcon />
          </ListItemIcon>
          <ListItemText primary={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'} />
        </MenuItem>
      ) : null}
    </>
  ) : null

  /** Tiny screens collapse the crumbs into a path button, so the title rides beside it; elsewhere the star does */
  const titleOrStar = isTiny ? (
    <span className={styles.title}>{entity?.name || 'Untitled'}</span>
  ) : entity && !foldIntoMenu ? (
    <FavoriteIcon entity={entity} />
  ) : null

  return (
    <>
      <Header
        ref={ref}
        className={foldIntoMenu ? styles.compactBar : undefined}
        position="absolute"
        elevation={0}
        controls={[
          showAgentsInBar ? (
            <span key="agents" className={styles.agents}>
              {additionalControls}
            </span>
          ) : null,
          showAgentsInBar ? (
            <span key="agents-divider" className={styles.divider} data-divider="agents" aria-hidden="true" />
          ) : null,
          showCreateButton && entity ? <CreateEntityButton key="create" parent={entity} /> : null,
          showLayoutSelector && !foldIntoMenu ? (
            <LayoutSelector
              key="layout"
              selected={layout ?? undefined}
              layouts={selectorLayouts}
              onChange={navigateToLayout}
              showIcon={false}
              aria-label={`Layout: ${layout?.name ?? ''}`}
              className={styles.layoutSelector}
            />
          ) : null,
          showShareButton && !foldIntoMenu ? (
            <Button key="share" variant="text" color="inherit" className={styles.textButton} onClick={showShare}>
              Share
            </Button>
          ) : null,
          showEditToggle ? (
            <ToggleButton
              key="edit"
              size="small"
              value="edit"
              selected={edit}
              className={styles.editToggle}
              aria-label="Enable edit mode"
              onClick={handleToggleEdit}
            >
              Edit
            </ToggleButton>
          ) : null,
          showPerformanceIcon ? (
            <Button
              key="perform"
              variant="contained"
              color="primary"
              size="small"
              className={cn(styles.performButton, isSmallOrSmaller && styles.performCompact)}
              // Compact on small screens: icon only, so it carries its name
              aria-label={isSmallOrSmaller ? 'Perform' : undefined}
              startIcon={isSmallOrSmaller ? undefined : <PerformanceIcon />}
              onClick={() => setLayout(performanceLayoutName)}
            >
              {isSmallOrSmaller ? <PerformanceIcon /> : 'Perform'}
            </Button>
          ) : null,

          <Presence key="presence" entityId={entityId} className={styles.presence} />,
          <EntityMenuButton
            key="menu"
            entity={entity}
            className={styles.menuButton}
            leadingItems={leadingMenuItems}
            triggerClassName={foldIntoMenu ? styles.moreCompact : undefined}
            unreadDot={foldIntoMenu && unreadAgents > 0}
            aria-label={moreLabel}
          />,
        ].filter(Boolean)}
        showBackButton={container?.showBreadcrumbs !== false}
        breadcrumbs={
          container?.showBreadcrumbs !== false ? (
            <TunaEntityBreadcrumbs
              // Menu mode: the path button keeps its size and the title takes the free space
              className={isTiny ? undefined : styles.breadcrumbs}
              entity={entity}
              showHome={true}
              mode={isTiny ? 'menu' : 'inline'}
              trailing={titleOrStar}
            />
          ) : undefined
        }
      />
      {shareDialog}
    </>
  )
}
